export interface HrAnalyticsSnapshot {
  analyticsSnapshotId?: number;
  owningCompanyId?: number;
  reportPeriodId?: number;
  metricCode?: string;
  metricCategory?: string;
  metricUnit?: string;
  snapshotDate?: string;
  periodStart?: string;
  periodEnd?: string;
  segmentType?: string;
  segmentCode?: string;
  metricValue?: number;
  comparisonValue?: number;
  trendDirection?: string;
  riskLevel?: string;
  scorecardStatus?: string;
  dataQualityStatus?: string;
  sourceCount?: number;
  calculationVersion?: string;
  drilldownReference?: string;
  sourceObjectType?: string;
  sourceObjectId?: number;
  assumptionsJson?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrWorkforceForecastScenario {
  workforceForecastScenarioId?: number;
  owningCompanyId?: number;
  scenarioCode?: string;
  scenarioName?: string;
  scenarioStatus?: string;
  forecastCategory?: string;
  periodStart?: string;
  periodEnd?: string;
  demandBasis?: string;
  requiredHeadcount?: number;
  availableHeadcount?: number;
  gapHeadcount?: number;
  overtimeHoursRisk?: number;
  complianceRiskLevel?: string;
  assumptionsJson?: string;
  recommendationText?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrWorkforceOptimizationRecommendation {
  workforceOptimizationRecommendationId?: number;
  owningCompanyId?: number;
  recommendationCode?: string;
  recommendationType?: string;
  recommendationStatus?: string;
  priority?: string;
  title?: string;
  description?: string;
  sourceCategory?: string;
  sourceObjectType?: string;
  sourceObjectId?: number;
  estimatedImpactJson?: string;
  humanApprovalRequired?: boolean;
  approvedByCustomerId?: number;
  approvedAt?: string;
  dismissedByCustomerId?: number;
  dismissedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrAnalyticsDashboard {
  owningCompanyId?: number;
  totalEmployees?: number;
  activeEmployees?: number;
  draftEmployees?: number;
  onboardingEmployees?: number;
  analyticsSnapshots?: number;
  forecastScenarios?: number;
  openRecommendations?: number;
  latestSnapshots?: HrAnalyticsSnapshot[];
  forecasts?: HrWorkforceForecastScenario[];
  recommendations?: HrWorkforceOptimizationRecommendation[];
}

export interface HrAnalyticsExport {
  owningCompanyId?: number;
  exportFormat?: string;
  exportName?: string;
  snapshots?: HrAnalyticsSnapshot[];
  forecasts?: HrWorkforceForecastScenario[];
  recommendations?: HrWorkforceOptimizationRecommendation[];
}

export interface HrAiContext {
  owningCompanyId?: number;
  contextScope?: string;
  permissionPolicy?: string;
  dataSensitivity?: string;
  allowedSignals?: string[];
  blockedSignals?: string[];
  analyticsSignals?: HrAnalyticsSnapshot[];
  recommendations?: HrWorkforceOptimizationRecommendation[];
}

export const HR_ANALYTICS_CATEGORIES = ['HEADCOUNT', 'TURNOVER', 'ABSENCE', 'PAYROLL', 'READINESS', 'TRAINING', 'ROSTER'];

export const HR_FORECAST_CATEGORIES = ['DRIVER_DEMAND', 'WAREHOUSE_LABOR', 'CLEARANCE_BROKER', 'OVERTIME_RISK', 'COMPLIANCE_RISK', 'WORKFORCE'];

export const HR_RECOMMENDATION_STATUSES = ['OPEN', 'APPROVED', 'DISMISSED'];

export const HR_RECOMMENDATION_PRIORITIES = ['HIGH', 'MEDIUM', 'LOW'];

export interface HrAnnualReportScope {
  owningCompanyId?: number;
  companyGroupId?: number;
  memberCompanyId?: number;
  scopeName?: string;
}

export interface HrAnalyticsReportPeriod {
  analyticsReportPeriodId?: number;
  owningCompanyId?: number;
  reportYear?: number;
  reportMonth?: number;
  periodStart?: string;
  periodEnd?: string;
  periodStatus?: 'DRAFT' | 'REVIEWED' | 'LOCKED';
  calculationVersion?: string;
  sourceWatermark?: string;
  generatedAt?: string;
  generatedByCustomerId?: number;
  reviewedAt?: string;
  reviewedByCustomerId?: number;
  lockedAt?: string;
  lockedByCustomerId?: number;
  reopenedAt?: string;
  reopenedByCustomerId?: number;
  reopenReason?: string;
}

export interface HrAnnualMetricMonth {
  month: number;
  value?: number;
  displayValue?: string;
  scorecardStatus?: 'GREEN' | 'AMBER' | 'RED' | 'NOT_AVAILABLE';
  calculatedStatus?: string;
  dataQualityStatus?: 'COMPLETE' | 'PARTIAL' | 'MISSING';
  sourceCount?: number;
  periodStatus?: string;
  assumptionsJson?: string;
  drilldownReference?: string;
  overridden?: boolean;
  overrideReason?: string;
}

export interface HrAnnualMetricSeries {
  metricCode: string;
  metricName: string;
  metricCategory: string;
  metricUnit: string;
  sortOrder?: number;
  months: HrAnnualMetricMonth[];
}

export interface HrCompanyAnnualReport {
  owningCompanyId?: number;
  companyName?: string;
  consolidated?: boolean;
  periods?: HrAnalyticsReportPeriod[];
  metrics?: HrAnnualMetricSeries[];
}

export interface HrAnnualMetricDefinition {
  analyticsMetricDefinitionId?: number;
  metricCode: string;
  metricName: string;
  metricCategory: string;
  metricUnit: string;
  calculationVersion?: string;
  calculationDescription?: string;
  sortOrder?: number;
}

export interface HrAnnualDataQualityIssue {
  owningCompanyId?: number;
  companyName?: string;
  month?: number;
  metricCode?: string;
  severity?: string;
  description?: string;
  affectedRecords?: number;
}

export interface HrAnnualManagementReport {
  year?: number;
  groupScope?: boolean;
  scopeName?: string;
  generatedAt?: string;
  companies?: HrCompanyAnnualReport[];
  consolidated?: HrCompanyAnnualReport;
  metricDefinitions?: HrAnnualMetricDefinition[];
  dataQualityIssues?: HrAnnualDataQualityIssue[];
}

export interface HrAnalyticsScorecardRule {
  analyticsScorecardRuleId?: number;
  owningCompanyId?: number;
  metricCode?: string;
  direction?: 'HIGHER_IS_BETTER' | 'LOWER_IS_BETTER' | 'BOOLEAN';
  greenThreshold?: number;
  amberThreshold?: number;
  active?: boolean;
}

export interface HrAnnualMetricDetails {
  owningCompanyId?: number;
  year?: number;
  month?: number;
  metricCode?: string;
  value?: number;
  metricUnit?: string;
  scorecardStatus?: string;
  dataQualityStatus?: string;
  sourceCount?: number;
  calculationDescription?: string;
  assumptionsJson?: string;
  drilldownReference?: string;
  sourceRecords?: HrAnnualSourceRecord[];
}

export interface HrAnnualSourceRecord {
  sourceType?: string;
  sourceId?: number;
  employeeId?: number;
  label?: string;
  status?: string;
  recordDate?: string;
  value?: string;
}
